type ClientAddress = {
  street?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
};

type ClientGeo = {
  geoCity?: string | null;
  geoRegion?: string | null;
  geoCountry?: string | null;
};

export type ClientDisplaySource = ClientGeo & {
  name?: string | null;
  address?: ClientAddress | null;
};

export const CLIENT_FALLBACK_NAME = "Klijent";
export const CLIENT_FALLBACK_LOCATION = "Lokacija nije dostupna";

function normalizeText(value?: string | null) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function joinNonEmpty(parts: Array<string | null | undefined>) {
  const resolved = parts.filter((part): part is string => Boolean(part));
  return resolved.length > 0 ? resolved.join(", ") : null;
}

export function getClientDisplayName(
  source?: Pick<ClientDisplaySource, "name"> | null,
  fallback = CLIENT_FALLBACK_NAME,
) {
  return normalizeText(source?.name) ?? fallback;
}

export function getClientDisplayInitial(
  source?: Pick<ClientDisplaySource, "name"> | null,
  fallback = CLIENT_FALLBACK_NAME,
) {
  return getClientDisplayName(source, fallback).charAt(0).toUpperCase();
}

export function getClientAddressLine(
  address?: ClientAddress | null,
  fallback: string | null = null,
) {
  return (
    joinNonEmpty([
      normalizeText(address?.street),
      normalizeText(address?.postalCode),
      normalizeText(address?.city),
      normalizeText(address?.country),
    ]) ?? fallback
  );
}

export function getClientAddressLocation(
  address?: Pick<ClientAddress, "city" | "country"> | null,
  fallback: string | null = null,
) {
  return (
    joinNonEmpty([
      normalizeText(address?.city),
      normalizeText(address?.country),
    ]) ?? fallback
  );
}

export function getClientGeoLocation(
  source?: ClientGeo | null,
  options?: { includeRegion?: boolean; fallback?: string | null },
) {
  return (
    joinNonEmpty([
      normalizeText(source?.geoCity),
      options?.includeRegion ? normalizeText(source?.geoRegion) : null,
      normalizeText(source?.geoCountry),
    ]) ??
    options?.fallback ??
    null
  );
}

export function getClientDisplayLocation(
  source?: Pick<
    ClientDisplaySource,
    "address" | "geoCity" | "geoRegion" | "geoCountry"
  > | null,
  options?: { includeRegion?: boolean; fallback?: string | null },
) {
  return (
    getClientAddressLocation(source?.address) ??
    getClientGeoLocation(source, {
      includeRegion: options?.includeRegion,
    }) ??
    options?.fallback ??
    CLIENT_FALLBACK_LOCATION
  );
}
